import { useState, useEffect, useRef } from 'react'
import PostList from '../components/PostList'
import Pagination from '../components/Pagination'
import Filters from '../components/Filters'
import { buildRequestURL, parseWordpressPosts, queryParams } from '../lib/helpers'

const Blog = ({ perPage, apiDomain, apiPath, initialAuthors }) => {

    const [posts, setPosts] = useState([])
    const [filteredPosts, setFilteredPosts] = useState([])
    const [pageOfPosts, setPageOfPosts] = useState([])
    const [categories, setCategories] = useState([])
    const [authors, setAuthors] = useState([])
    const [activePage, setActivePage] = useState(1)
    const [isLoadingPosts, setIsLoadingPosts] = useState(true)
    const [isLoadingCategories, setIsLoadingCategories] = useState(true)
    const [isLoadingAuthors, setIsLoadingAuthors] = useState(false)
    const [filters, setFilters] = useState({
        category: 0,
        author: 0,
        search: ''
    })

    const [totalPosts, setTotalPosts] = useState(0)
    const [isLoading, setIsLoading] = useState(true)

    // Reference to main app div to avoid searching the DOM for it each render
    const appRef = useRef()

    const updatePageOfPosts = (page) => {
        const pageStart = (page - 1) * perPage
        const pageEnd = pageStart + perPage
        setActivePage(page)
        setPageOfPosts(filteredPosts.slice(pageStart, pageEnd))
    }

    const loadMore = () => {
        setIsLoadingPosts(true)
        const loadurl = buildRequestURL({
            apiDomain: apiDomain,
            apiPath: apiPath + '/posts',
            perPage: 12,
            offset: posts.length
        })
        console.log(loadurl)
        fetch(loadurl)
            .then(response => {
                if ( typeof response.status !== 'undefined' && response.status === 200 ) {
                    return response.json()
                }
                throw response
            })
            .then(data => {
                setPosts(posts => {
                    return [...posts, ...data]
                })
            })
            .catch(error => {
                console.error('Error fetching reviews')
                console.error(error)
            })
            .finally(() => {
                setIsLoadingPosts(false)
            })
    }

    const fetchPosts = () => {
        setIsLoadingPosts(true)
        const url = buildRequestURL({
            apiDomain: apiDomain,
            apiPath: apiPath + '/posts',
            perPage: 12,
        })
        console.log(url)
        fetch(url)
            .then(response => {
                const totalPosts = parseInt( response.headers.get('x-wp-total') )
                console.log(totalPosts)
                setTotalPosts(totalPosts)
                if ( typeof response.status !== 'undefined' && response.status === 200 ) {
                    return response.json()
                }
                throw response
            })
            .then(rawData => {
                return parseWordpressPosts(rawData)
            })
            .then(data => {
                setPosts(data)
            })
            .catch(error => {
                console.error('Error fetching posts', error)
            })
            .finally(() => {
                setIsLoadingPosts(false)
            })
    }

    const fetchCategories = () => {

        const url = buildRequestURL({
            apiDomain: apiDomain,
            apiPath: apiPath + '/categories'
        })

        fetch(url)
            .then(response => {
                if ( typeof response.status !== 'undefined' && response.status === 200 ) {
                    return response.json()
                }
                throw response
            })
            .then(data => {
                setCategories(data)
            })
            .catch(error => {
                console.error('Error fetching categories', error)
            })
            .finally(() => {

                setIsLoadingCategories(false)
            })
    }

    const fetchAuthors = () => {

        const url = buildRequestURL({
            apiDomain: apiDomain,
            apiPath: apiPath + '/users?has_published_posts=true&per_page=100'
        })

        fetch(url)
            .then(response => {
                if ( typeof response.status !== 'undefined' && response.status === 200 ) {
                    return response.json()
                }
                throw response
            })
            .then(data => {
                setAuthors(data)
            })
            .catch(error => {
                console.error('Error fetching authors', error)
            })
            .finally(() => {
                setIsLoadingAuthors(false)
            })
    }

    // const fetchBrands = () => {
    //
    //     const url = buildRequestURL({
    //         apiDomain: apiDomain,
    //         apiPath: apiPath + '/posts-brands'
    //     })
    //
    //     fetch(url)
    //         .then(response => {
    //             if ( typeof response.status !== 'undefined' && response.status === 200 ) {
    //                 return response.json()
    //             }
    //             throw response
    //         })
    //         .then(data => {
    //             setBrands(data)
    //         })
    //         .catch(error => {
    //             console.error('Error fetching brands', error)
    //         })
    //         .finally(() => {
    //             setIsLoadingBrands(false)
    //         })
    // }

    const updateFilter = (name, value) => {
        switch ( name ) {
            case 'blog-filters__category':
                setFilters((filters) => {
                    const intValue = parseInt(value)
                    return isNaN(intValue)
                        ? { ...filters, category: 0 }
                        : { ...filters, category: parseInt(value) }
                })
                break;
            case 'blog-filters__author':
                setFilters((filters) => {
                    const intValue = parseInt(value)
                    return isNaN(intValue)
                        ? { ...filters, author: 0 }
                        : { ...filters, author: parseInt(value) }
                })
                break;
            // Can use memo to cache
            case "blog-filters__search":
                setFilters((filters) => {
                    return { ...filters, search: value }
                })
        }
        setActivePage(1)
    }

    // Scroll to top of main app div

    const scrollToTop = () => {
        appRef.current.scrollIntoView({
            behavior: "smooth",
            block: "start"
        })
    }

    const handlePaginationButtonClick = (page) => {
        updatePageOfPosts(page)
        setTimeout(scrollToTop, 10)
    }

    // Update initial category value when categories loaded

    useEffect(() => {
        if (!isLoadingCategories) {
            const initialCategory = categories.filter(category => {
                return category.slug === queryParams.category
            })
            if (typeof initialCategory[0] !== 'undefined' && initialCategory[0]) {
                updateFilter("blog-filters__category", initialCategory[0].id)
            }
        }
    }, [isLoadingCategories])

    useEffect(() => {
        if (!isLoadingAuthors) {
            const initialAuthor = authors.filter(author => {
                return author.slug === queryParams.author
            })
            if (typeof initialAuthor[0] !== 'undefined' && initialAuthor[0]) {
                updateFilter("blog-filters__author", initialAuthor[0].id)
            }
        }
    }, [isLoadingAuthors])

    useEffect(() => {
        if (typeof queryParams.s !== "undefined" && queryParams.s !== null) {
            updateFilter("blog-filters__search", queryParams.s)
        }
    }, [])

    // Update initial brand value when categories loaded

    // useEffect(() => {
    //     if (!isLoadingBrands) {
    //         const initialBrand = brands.filter(brand => {
    //             return brand.slug === queryParams.brand
    //         })
    //         if (typeof initialBrand[0] !== 'undefined' && initialBrand[0]) {
    //             updateFilter("blog-filters__brand", initialBrand[0].id)
    //         }
    //     }
    // }, [isLoadingBrands])

    // Filter posts when filters change

    useEffect(() => {
        setFilteredPosts(posts.filter((post) => {
            const categoryFilter = filters.category === 0 || post.categories.includes(filters.category)
            const searchFilter = filters.search === '' || post.title.rendered.toLowerCase().includes(filters.search.toLowerCase())
            // const brandFilter = filters.brand === 0 || post["posts-brands"].includes(filters.brand)
            const authorFilter = filters.author === 0 || post.author === filters.author
            return ( categoryFilter && searchFilter && authorFilter)
        }))
    }, [posts, filters])

    // Set pageOfPosts when posts array changes

    useEffect(() => {
        updatePageOfPosts(activePage)
    }, [filteredPosts])

    // Fetch initial data from server on page load

    useEffect(() => {
        fetchPosts()
        fetchCategories()
        // fetchBrands()
        if (initialAuthors) {
            const json_data = atob(initialAuthors)
            setAuthors(JSON.parse(json_data))
        } else {
            fetchAuthors()
        }
    }, [])

    return (
        <div>
            <div className="ls-react-blog-anchor" ref={appRef}></div>
            { isLoadingPosts && (
            <>
                <div id="blog-filters-category" className="category-field">
                    <h2>Latest</h2>
                </div>
                <div className="blog-filter">
                    <div className="skeleton skeleton-select"></div>
                </div>
                <div className="post-list posts-wrapper">
                    <article className="tease-post-container">
                        <a className="post-wrapper">
                            <div className="skeleton post-image skeleton__image">
                                <div className="image-inner">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="38" height="36" viewBox="0 0 38 36" fill="none">
                                        <g clip-path="url(#clip0_2953_1727)">
                                            <path d="M34.2 0H3.8C2.79218 0 1.82563 0.421427 1.11299 1.17157C0.400356 1.92172 0 2.93913 0 4L0 32C0 33.0609 0.400356 34.0783 1.11299 34.8284C1.82563 35.5786 2.79218 36 3.8 36H34.2C35.2078 36 36.1744 35.5786 36.887 34.8284C37.5996 34.0783 38 33.0609 38 32V4C38 2.93913 37.5996 1.92172 36.887 1.17157C36.1744 0.421427 35.2078 0 34.2 0ZM23.75 8C24.1243 8 24.4949 8.0776 24.8406 8.22836C25.1864 8.37913 25.5006 8.6001 25.7653 8.87868C26.0299 9.15726 26.2398 9.48797 26.3831 9.85195C26.5263 10.2159 26.6 10.606 26.6 11C26.6 11.394 26.5263 11.7841 26.3831 12.1481C26.2398 12.512 26.0299 12.8427 25.7653 13.1213C25.5006 13.3999 25.1864 13.6209 24.8406 13.7716C24.4949 13.9224 24.1243 14 23.75 14C22.9941 14 22.2692 13.6839 21.7347 13.1213C21.2003 12.5587 20.9 11.7956 20.9 11C20.9 10.2044 21.2003 9.44129 21.7347 8.87868C22.2692 8.31607 22.9941 8 23.75 8ZM32.0644 28.962C31.9007 29.2761 31.6599 29.5381 31.3671 29.7207C31.0743 29.9033 30.7403 29.9997 30.4 30H7.6C7.2761 30.0002 6.95753 29.9132 6.67456 29.7472C6.39159 29.5813 6.15361 29.342 5.98323 29.0521C5.81285 28.7621 5.71571 28.4311 5.70106 28.0905C5.68641 27.7499 5.75471 27.411 5.8995 27.106L12.5495 13.106C12.7026 12.7831 12.9358 12.5098 13.2245 12.315C13.5132 12.1203 13.8465 12.0115 14.1892 12C14.5348 11.9798 14.8789 12.0629 15.1815 12.2399C15.4841 12.4169 15.7329 12.6804 15.8992 13L21.1717 22.514L24.1091 18.74C24.2999 18.4954 24.5431 18.3019 24.8188 18.1755C25.0944 18.0491 25.3947 17.9934 25.6948 18.0129C25.9949 18.0325 26.2863 18.1267 26.5451 18.2878C26.8038 18.449 27.0226 18.6725 27.1833 18.94L32.0112 26.94C32.19 27.2416 32.2892 27.588 32.2985 27.9434C32.3079 28.2988 32.2271 28.6504 32.0644 28.962Z" fill="#BCBEC0"/>
                                        </g>
                                        <defs>
                                            <clipPath id="clip0_2953_1727">
                                                <rect width="38" height="36" fill="white"/>
                                            </clipPath>
                                        </defs>
                                    </svg>
                                </div>
                            </div>
                            <div className="skeleton skeleton-text skeleton__categories"></div>
                            <div className="skeleton__title">
                                <div className="skeleton skeleton-text"></div>
                                <div className="skeleton skeleton-text"></div>
                                <div className="skeleton skeleton-text"></div>
                            </div>
                            <div className="skeleton__date skeleton skeleton-text"></div>
                            <div className="skeleton__button skeleton skeleton-text"></div>
                        </a>
                    </article>
                    <article className="tease-post-container">
                        <a className="post-wrapper">
                            <div className="skeleton post-image skeleton__image">
                                <div className="image-inner">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="38" height="36" viewBox="0 0 38 36" fill="none">
                                        <g clip-path="url(#clip0_2953_1727)">
                                            <path d="M34.2 0H3.8C2.79218 0 1.82563 0.421427 1.11299 1.17157C0.400356 1.92172 0 2.93913 0 4L0 32C0 33.0609 0.400356 34.0783 1.11299 34.8284C1.82563 35.5786 2.79218 36 3.8 36H34.2C35.2078 36 36.1744 35.5786 36.887 34.8284C37.5996 34.0783 38 33.0609 38 32V4C38 2.93913 37.5996 1.92172 36.887 1.17157C36.1744 0.421427 35.2078 0 34.2 0ZM23.75 8C24.1243 8 24.4949 8.0776 24.8406 8.22836C25.1864 8.37913 25.5006 8.6001 25.7653 8.87868C26.0299 9.15726 26.2398 9.48797 26.3831 9.85195C26.5263 10.2159 26.6 10.606 26.6 11C26.6 11.394 26.5263 11.7841 26.3831 12.1481C26.2398 12.512 26.0299 12.8427 25.7653 13.1213C25.5006 13.3999 25.1864 13.6209 24.8406 13.7716C24.4949 13.9224 24.1243 14 23.75 14C22.9941 14 22.2692 13.6839 21.7347 13.1213C21.2003 12.5587 20.9 11.7956 20.9 11C20.9 10.2044 21.2003 9.44129 21.7347 8.87868C22.2692 8.31607 22.9941 8 23.75 8ZM32.0644 28.962C31.9007 29.2761 31.6599 29.5381 31.3671 29.7207C31.0743 29.9033 30.7403 29.9997 30.4 30H7.6C7.2761 30.0002 6.95753 29.9132 6.67456 29.7472C6.39159 29.5813 6.15361 29.342 5.98323 29.0521C5.81285 28.7621 5.71571 28.4311 5.70106 28.0905C5.68641 27.7499 5.75471 27.411 5.8995 27.106L12.5495 13.106C12.7026 12.7831 12.9358 12.5098 13.2245 12.315C13.5132 12.1203 13.8465 12.0115 14.1892 12C14.5348 11.9798 14.8789 12.0629 15.1815 12.2399C15.4841 12.4169 15.7329 12.6804 15.8992 13L21.1717 22.514L24.1091 18.74C24.2999 18.4954 24.5431 18.3019 24.8188 18.1755C25.0944 18.0491 25.3947 17.9934 25.6948 18.0129C25.9949 18.0325 26.2863 18.1267 26.5451 18.2878C26.8038 18.449 27.0226 18.6725 27.1833 18.94L32.0112 26.94C32.19 27.2416 32.2892 27.588 32.2985 27.9434C32.3079 28.2988 32.2271 28.6504 32.0644 28.962Z" fill="#BCBEC0"/>
                                        </g>
                                        <defs>
                                            <clipPath id="clip0_2953_1727">
                                                <rect width="38" height="36" fill="white"/>
                                            </clipPath>
                                        </defs>
                                    </svg>
                                </div>
                            </div>
                            <div className="skeleton skeleton-text skeleton__categories"></div>
                            <div className="skeleton__title">
                                <div className="skeleton skeleton-text"></div>
                                <div className="skeleton skeleton-text"></div>
                                <div className="skeleton skeleton-text"></div>
                            </div>
                            <div className="skeleton__date skeleton skeleton-text"></div>
                            <div className="skeleton__button skeleton skeleton-text"></div>
                        </a>
                    </article>
                    <article className="tease-post-container">
                        <a className="post-wrapper">
                            <div className="skeleton post-image skeleton__image">
                                <div className="image-inner">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="38" height="36" viewBox="0 0 38 36" fill="none">
                                        <g clip-path="url(#clip0_2953_1727)">
                                            <path d="M34.2 0H3.8C2.79218 0 1.82563 0.421427 1.11299 1.17157C0.400356 1.92172 0 2.93913 0 4L0 32C0 33.0609 0.400356 34.0783 1.11299 34.8284C1.82563 35.5786 2.79218 36 3.8 36H34.2C35.2078 36 36.1744 35.5786 36.887 34.8284C37.5996 34.0783 38 33.0609 38 32V4C38 2.93913 37.5996 1.92172 36.887 1.17157C36.1744 0.421427 35.2078 0 34.2 0ZM23.75 8C24.1243 8 24.4949 8.0776 24.8406 8.22836C25.1864 8.37913 25.5006 8.6001 25.7653 8.87868C26.0299 9.15726 26.2398 9.48797 26.3831 9.85195C26.5263 10.2159 26.6 10.606 26.6 11C26.6 11.394 26.5263 11.7841 26.3831 12.1481C26.2398 12.512 26.0299 12.8427 25.7653 13.1213C25.5006 13.3999 25.1864 13.6209 24.8406 13.7716C24.4949 13.9224 24.1243 14 23.75 14C22.9941 14 22.2692 13.6839 21.7347 13.1213C21.2003 12.5587 20.9 11.7956 20.9 11C20.9 10.2044 21.2003 9.44129 21.7347 8.87868C22.2692 8.31607 22.9941 8 23.75 8ZM32.0644 28.962C31.9007 29.2761 31.6599 29.5381 31.3671 29.7207C31.0743 29.9033 30.7403 29.9997 30.4 30H7.6C7.2761 30.0002 6.95753 29.9132 6.67456 29.7472C6.39159 29.5813 6.15361 29.342 5.98323 29.0521C5.81285 28.7621 5.71571 28.4311 5.70106 28.0905C5.68641 27.7499 5.75471 27.411 5.8995 27.106L12.5495 13.106C12.7026 12.7831 12.9358 12.5098 13.2245 12.315C13.5132 12.1203 13.8465 12.0115 14.1892 12C14.5348 11.9798 14.8789 12.0629 15.1815 12.2399C15.4841 12.4169 15.7329 12.6804 15.8992 13L21.1717 22.514L24.1091 18.74C24.2999 18.4954 24.5431 18.3019 24.8188 18.1755C25.0944 18.0491 25.3947 17.9934 25.6948 18.0129C25.9949 18.0325 26.2863 18.1267 26.5451 18.2878C26.8038 18.449 27.0226 18.6725 27.1833 18.94L32.0112 26.94C32.19 27.2416 32.2892 27.588 32.2985 27.9434C32.3079 28.2988 32.2271 28.6504 32.0644 28.962Z" fill="#BCBEC0"/>
                                        </g>
                                        <defs>
                                            <clipPath id="clip0_2953_1727">
                                                <rect width="38" height="36" fill="white"/>
                                            </clipPath>
                                        </defs>
                                    </svg>
                                </div>
                            </div>
                            <div className="skeleton skeleton-text skeleton__categories"></div>
                            <div className="skeleton__title">
                                <div className="skeleton skeleton-text"></div>
                                <div className="skeleton skeleton-text"></div>
                                <div className="skeleton skeleton-text"></div>
                            </div>
                            <div className="skeleton__date skeleton skeleton-text"></div>
                            <div className="skeleton__button skeleton skeleton-text"></div>
                        </a>
                    </article>
                    <article className="tease-post-container">
                        <a className="post-wrapper">
                            <div className="skeleton post-image skeleton__image">
                                <div className="image-inner">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="38" height="36" viewBox="0 0 38 36" fill="none">
                                        <g clip-path="url(#clip0_2953_1727)">
                                            <path d="M34.2 0H3.8C2.79218 0 1.82563 0.421427 1.11299 1.17157C0.400356 1.92172 0 2.93913 0 4L0 32C0 33.0609 0.400356 34.0783 1.11299 34.8284C1.82563 35.5786 2.79218 36 3.8 36H34.2C35.2078 36 36.1744 35.5786 36.887 34.8284C37.5996 34.0783 38 33.0609 38 32V4C38 2.93913 37.5996 1.92172 36.887 1.17157C36.1744 0.421427 35.2078 0 34.2 0ZM23.75 8C24.1243 8 24.4949 8.0776 24.8406 8.22836C25.1864 8.37913 25.5006 8.6001 25.7653 8.87868C26.0299 9.15726 26.2398 9.48797 26.3831 9.85195C26.5263 10.2159 26.6 10.606 26.6 11C26.6 11.394 26.5263 11.7841 26.3831 12.1481C26.2398 12.512 26.0299 12.8427 25.7653 13.1213C25.5006 13.3999 25.1864 13.6209 24.8406 13.7716C24.4949 13.9224 24.1243 14 23.75 14C22.9941 14 22.2692 13.6839 21.7347 13.1213C21.2003 12.5587 20.9 11.7956 20.9 11C20.9 10.2044 21.2003 9.44129 21.7347 8.87868C22.2692 8.31607 22.9941 8 23.75 8ZM32.0644 28.962C31.9007 29.2761 31.6599 29.5381 31.3671 29.7207C31.0743 29.9033 30.7403 29.9997 30.4 30H7.6C7.2761 30.0002 6.95753 29.9132 6.67456 29.7472C6.39159 29.5813 6.15361 29.342 5.98323 29.0521C5.81285 28.7621 5.71571 28.4311 5.70106 28.0905C5.68641 27.7499 5.75471 27.411 5.8995 27.106L12.5495 13.106C12.7026 12.7831 12.9358 12.5098 13.2245 12.315C13.5132 12.1203 13.8465 12.0115 14.1892 12C14.5348 11.9798 14.8789 12.0629 15.1815 12.2399C15.4841 12.4169 15.7329 12.6804 15.8992 13L21.1717 22.514L24.1091 18.74C24.2999 18.4954 24.5431 18.3019 24.8188 18.1755C25.0944 18.0491 25.3947 17.9934 25.6948 18.0129C25.9949 18.0325 26.2863 18.1267 26.5451 18.2878C26.8038 18.449 27.0226 18.6725 27.1833 18.94L32.0112 26.94C32.19 27.2416 32.2892 27.588 32.2985 27.9434C32.3079 28.2988 32.2271 28.6504 32.0644 28.962Z" fill="#BCBEC0"/>
                                        </g>
                                        <defs>
                                            <clipPath id="clip0_2953_1727">
                                                <rect width="38" height="36" fill="white"/>
                                            </clipPath>
                                        </defs>
                                    </svg>
                                </div>
                            </div>
                            <div className="skeleton skeleton-text skeleton__categories"></div>
                            <div className="skeleton__title">
                                <div className="skeleton skeleton-text"></div>
                                <div className="skeleton skeleton-text"></div>
                                <div className="skeleton skeleton-text"></div>
                            </div>
                            <div className="skeleton__date skeleton skeleton-text"></div>
                            <div className="skeleton__button skeleton skeleton-text"></div>
                        </a>
                    </article>
                    <article className="tease-post-container">
                        <a className="post-wrapper">
                            <div className="skeleton post-image skeleton__image">
                                <div className="image-inner">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="38" height="36" viewBox="0 0 38 36" fill="none">
                                        <g clip-path="url(#clip0_2953_1727)">
                                            <path d="M34.2 0H3.8C2.79218 0 1.82563 0.421427 1.11299 1.17157C0.400356 1.92172 0 2.93913 0 4L0 32C0 33.0609 0.400356 34.0783 1.11299 34.8284C1.82563 35.5786 2.79218 36 3.8 36H34.2C35.2078 36 36.1744 35.5786 36.887 34.8284C37.5996 34.0783 38 33.0609 38 32V4C38 2.93913 37.5996 1.92172 36.887 1.17157C36.1744 0.421427 35.2078 0 34.2 0ZM23.75 8C24.1243 8 24.4949 8.0776 24.8406 8.22836C25.1864 8.37913 25.5006 8.6001 25.7653 8.87868C26.0299 9.15726 26.2398 9.48797 26.3831 9.85195C26.5263 10.2159 26.6 10.606 26.6 11C26.6 11.394 26.5263 11.7841 26.3831 12.1481C26.2398 12.512 26.0299 12.8427 25.7653 13.1213C25.5006 13.3999 25.1864 13.6209 24.8406 13.7716C24.4949 13.9224 24.1243 14 23.75 14C22.9941 14 22.2692 13.6839 21.7347 13.1213C21.2003 12.5587 20.9 11.7956 20.9 11C20.9 10.2044 21.2003 9.44129 21.7347 8.87868C22.2692 8.31607 22.9941 8 23.75 8ZM32.0644 28.962C31.9007 29.2761 31.6599 29.5381 31.3671 29.7207C31.0743 29.9033 30.7403 29.9997 30.4 30H7.6C7.2761 30.0002 6.95753 29.9132 6.67456 29.7472C6.39159 29.5813 6.15361 29.342 5.98323 29.0521C5.81285 28.7621 5.71571 28.4311 5.70106 28.0905C5.68641 27.7499 5.75471 27.411 5.8995 27.106L12.5495 13.106C12.7026 12.7831 12.9358 12.5098 13.2245 12.315C13.5132 12.1203 13.8465 12.0115 14.1892 12C14.5348 11.9798 14.8789 12.0629 15.1815 12.2399C15.4841 12.4169 15.7329 12.6804 15.8992 13L21.1717 22.514L24.1091 18.74C24.2999 18.4954 24.5431 18.3019 24.8188 18.1755C25.0944 18.0491 25.3947 17.9934 25.6948 18.0129C25.9949 18.0325 26.2863 18.1267 26.5451 18.2878C26.8038 18.449 27.0226 18.6725 27.1833 18.94L32.0112 26.94C32.19 27.2416 32.2892 27.588 32.2985 27.9434C32.3079 28.2988 32.2271 28.6504 32.0644 28.962Z" fill="#BCBEC0"/>
                                        </g>
                                        <defs>
                                            <clipPath id="clip0_2953_1727">
                                                <rect width="38" height="36" fill="white"/>
                                            </clipPath>
                                        </defs>
                                    </svg>
                                </div>
                            </div>
                            <div className="skeleton skeleton-text skeleton__categories"></div>
                            <div className="skeleton__title">
                                <div className="skeleton skeleton-text"></div>
                                <div className="skeleton skeleton-text"></div>
                                <div className="skeleton skeleton-text"></div>
                            </div>
                            <div className="skeleton__date skeleton skeleton-text"></div>
                            <div className="skeleton__button skeleton skeleton-text"></div>
                        </a>
                    </article>
                    <article className="tease-post-container">
                        <a className="post-wrapper">
                            <div className="skeleton post-image skeleton__image">
                                <div className="image-inner">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="38" height="36" viewBox="0 0 38 36" fill="none">
                                        <g clip-path="url(#clip0_2953_1727)">
                                            <path d="M34.2 0H3.8C2.79218 0 1.82563 0.421427 1.11299 1.17157C0.400356 1.92172 0 2.93913 0 4L0 32C0 33.0609 0.400356 34.0783 1.11299 34.8284C1.82563 35.5786 2.79218 36 3.8 36H34.2C35.2078 36 36.1744 35.5786 36.887 34.8284C37.5996 34.0783 38 33.0609 38 32V4C38 2.93913 37.5996 1.92172 36.887 1.17157C36.1744 0.421427 35.2078 0 34.2 0ZM23.75 8C24.1243 8 24.4949 8.0776 24.8406 8.22836C25.1864 8.37913 25.5006 8.6001 25.7653 8.87868C26.0299 9.15726 26.2398 9.48797 26.3831 9.85195C26.5263 10.2159 26.6 10.606 26.6 11C26.6 11.394 26.5263 11.7841 26.3831 12.1481C26.2398 12.512 26.0299 12.8427 25.7653 13.1213C25.5006 13.3999 25.1864 13.6209 24.8406 13.7716C24.4949 13.9224 24.1243 14 23.75 14C22.9941 14 22.2692 13.6839 21.7347 13.1213C21.2003 12.5587 20.9 11.7956 20.9 11C20.9 10.2044 21.2003 9.44129 21.7347 8.87868C22.2692 8.31607 22.9941 8 23.75 8ZM32.0644 28.962C31.9007 29.2761 31.6599 29.5381 31.3671 29.7207C31.0743 29.9033 30.7403 29.9997 30.4 30H7.6C7.2761 30.0002 6.95753 29.9132 6.67456 29.7472C6.39159 29.5813 6.15361 29.342 5.98323 29.0521C5.81285 28.7621 5.71571 28.4311 5.70106 28.0905C5.68641 27.7499 5.75471 27.411 5.8995 27.106L12.5495 13.106C12.7026 12.7831 12.9358 12.5098 13.2245 12.315C13.5132 12.1203 13.8465 12.0115 14.1892 12C14.5348 11.9798 14.8789 12.0629 15.1815 12.2399C15.4841 12.4169 15.7329 12.6804 15.8992 13L21.1717 22.514L24.1091 18.74C24.2999 18.4954 24.5431 18.3019 24.8188 18.1755C25.0944 18.0491 25.3947 17.9934 25.6948 18.0129C25.9949 18.0325 26.2863 18.1267 26.5451 18.2878C26.8038 18.449 27.0226 18.6725 27.1833 18.94L32.0112 26.94C32.19 27.2416 32.2892 27.588 32.2985 27.9434C32.3079 28.2988 32.2271 28.6504 32.0644 28.962Z" fill="#BCBEC0"/>
                                        </g>
                                        <defs>
                                            <clipPath id="clip0_2953_1727">
                                                <rect width="38" height="36" fill="white"/>
                                            </clipPath>
                                        </defs>
                                    </svg>
                                </div>
                            </div>
                            <div className="skeleton skeleton-text skeleton__categories"></div>
                            <div className="skeleton__title">
                                <div className="skeleton skeleton-text"></div>
                                <div className="skeleton skeleton-text"></div>
                                <div className="skeleton skeleton-text"></div>
                            </div>
                            <div className="skeleton__date skeleton skeleton-text"></div>
                            <div className="skeleton__button skeleton skeleton-text"></div>
                        </a>
                    </article>

                </div>
            </>
            ) }
            { !isLoadingPosts && !isLoadingCategories && (
                <>
                    <Filters
                        category={filters.category}
                        author={filters.author}
                        authors={ authors }
                        search={filters.search}
                        categories={ categories }
                        updateFilter={ updateFilter }
                        onFilterChange={ updateFilter }
                    />
                    <PostList
                        posts={ pageOfPosts }
                        categories={ categories }
                        updateFilter={ updateFilter }
                    />
                </>
            ) }

            { !isLoadingPosts && posts.length < totalPosts && <div className="loadMoreWrapper"><button className="load-more-button" onClick={ loadMore }>Load more</button></div> }
        </div>
    )
}

export default Blog